import { useTableCellElementState } from 'lib-knowledge-document-structure/plate/table';
import {
  ComponentPropsWithoutRef,
  createContext,
  ReactNode,
  useContext,
} from 'react';
import { proxy, useSnapshot } from 'valtio';

type TableCellHoverState = {
  hoveredRowIndex: number;
  hoveredColumnIndex: number;
  menuShowing: {
    row: number;
    column: number;
    type: 'row' | 'column';
  } | null;
};

type Actions = {
  setHoveredIndex: (rowIndex: number, columnIndex: number) => void;
  clearHoveredIndex: (rowIndex: number, columnIndex: number) => void;
  setMenuShowing: (
    newMenuShowing: NonNullable<TableCellHoverState['menuShowing']>,
  ) => void;
  hideMenu: () => void;
};

export const TableCellHoverContext = createContext<{
  state: TableCellHoverState;
  actions: Actions;
  // @ts-expect-error - Must use a provider
}>(null);

export function TableCellHoverProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const state = proxy<TableCellHoverState>({
    hoveredRowIndex: -1,
    hoveredColumnIndex: -1,
    menuShowing: null,
  });

  const actions = {
    setHoveredIndex: (rowIndex: number, columnIndex: number): void => {
      state.hoveredRowIndex = rowIndex;
      state.hoveredColumnIndex = columnIndex;
    },
    clearHoveredIndex: (rowIndex: number, columnIndex: number): void => {
      // Don't clear if another cell is hovered
      if (
        state.hoveredColumnIndex === columnIndex &&
        state.hoveredRowIndex === rowIndex
      ) {
        state.hoveredRowIndex = -1;
        state.hoveredColumnIndex = -1;
      }
    },
    setMenuShowing: (
      newMenuShowing: NonNullable<TableCellHoverState['menuShowing']>,
    ): void => {
      state.menuShowing = newMenuShowing;
    },
    hideMenu: (): void => {
      state.menuShowing = null;
    },
  };

  return (
    <TableCellHoverContext.Provider value={{ state, actions }}>
      {children}
    </TableCellHoverContext.Provider>
  );
}

export type UseCellHoverReturn = {
  props: Partial<ComponentPropsWithoutRef<'td'>>;
  showRowButton: boolean;
  showColumnButton: boolean;
  showRowMenu: boolean;
  showColumnMenu: boolean;
  isRowSelected: boolean;
  isColumnSelected: boolean;
};

export function useCellHover(): UseCellHoverReturn {
  const { colIndex, rowIndex } = useTableCellElementState();

  const { state, actions } = useContext(TableCellHoverContext);

  const { hoveredRowIndex, hoveredColumnIndex, menuShowing } =
    useSnapshot(state);

  const showRowMenu =
    menuShowing?.type === 'row' &&
    menuShowing.row === rowIndex &&
    menuShowing.column === colIndex;

  const showColumnMenu =
    menuShowing?.type === 'column' &&
    menuShowing.row === rowIndex &&
    menuShowing.column === colIndex;

  return {
    showRowButton:
      menuShowing?.type !== 'column' &&
      (showRowMenu || (colIndex === 0 && hoveredRowIndex === rowIndex)),

    showColumnButton:
      menuShowing?.type !== 'row' &&
      (showColumnMenu || (rowIndex === 0 && hoveredColumnIndex === colIndex)),

    showRowMenu,
    showColumnMenu,
    isColumnSelected:
      menuShowing?.type === 'column' && menuShowing.column === colIndex,

    isRowSelected: menuShowing?.type === 'row' && menuShowing?.row === rowIndex,

    props: {
      onMouseEnter: () => {
        actions.setHoveredIndex(rowIndex, colIndex);
      },
      onMouseLeave: () => {
        actions.clearHoveredIndex(rowIndex, colIndex);
      },
    },
  };
}
